import { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Form, FormGroup, Label, Input } from "reactstrap";
import PageHeader from "../../../../components/PageHeader";
import ClientLogo from "../../client/ClientLogo";
import ClientLogoUpload from "../../client/ClientLogoUpload";
import {
  buildClientLogoBlobName,
  readFileAsBase64,
} from "../../client/clientUtils";
import { createClient, updateClient } from "../../../../services/client";
import { getCorporationData } from "../../../../services/corporation";

const PANEL_CARD =
  "w-full max-w-[640px] overflow-hidden rounded-lg border border-[#d3d3d3] bg-white pt-6 px-8 pb-8";
const CLIENT_INPUT_CLASS =
  "h-10 border border-[#d3d3d3] rounded px-3 text-[14px] leading-[22px] text-[#222222] outline-none focus:border-[#00A7A6] focus:shadow-none";

const SettingsClientCreateEditor = ({
  token,
  basePath = "/settings/clients",
}) => {
  const { t } = useTranslation();
  const history = useHistory();

  const [isSaving, setIsSaving] = useState(false);
  const [corporationId, setCorporationId] = useState(null);
  const [name, setName] = useState("");
  const [clientUniqueId, setClientUniqueId] = useState("");
  const [address, setAddress] = useState("");
  const [logoPreviewUrl, setLogoPreviewUrl] = useState("");
  const [logoFile, setLogoFile] = useState(null);
  const [logoFileName, setLogoFileName] = useState("");
  const [logoFileSize, setLogoFileSize] = useState(0);

  useEffect(() => {
    if (!token) return;
    getCorporationData()
      .then((res) => setCorporationId(res?.data?.corporation?._id ?? null))
      .catch(() => setCorporationId(null));
  }, [token]);

  const handleLogoRemove = useCallback(() => {
    setLogoPreviewUrl("");
    setLogoFile(null);
    setLogoFileName("");
    setLogoFileSize(0);
  }, []);

  const handleLogoSelect = async (file) => {
    setLogoFileName(file.name);
    setLogoFileSize(file.size);
    try {
      const base64File = await readFileAsBase64(file);
      setLogoPreviewUrl(base64File);
      setLogoFile({ base64File });
    } catch (error) {
      console.error(error);
      handleLogoRemove();
      toast.error(t("Failed to upload file"));
    }
  };

  const handleCreate = async (e) => {
    e?.preventDefault?.();
    if (!name.trim()) {
      toast.error(t("name_field_required"));
      return;
    }

    setIsSaving(true);
    try {
      const basePayload = {
        name: name.trim(),
        address: address.trim(),
        clientUniqueId: clientUniqueId.trim(),
        logoUrl: "",
      };
      const res = await createClient(basePayload);
      const created = res?.data;
      const createdId = created?._id;

      if (createdId && logoFile && corporationId) {
        const blobName = buildClientLogoBlobName(
          corporationId,
          createdId,
          logoFileName,
        );
        const logoPath = `https://cameraanalysis.blob.core.windows.net/ak-corporation/${blobName}`;
        await updateClient(createdId, {
          dataLineUpdate: {
            ...basePayload,
            logoUrl: logoPath,
            logoFile: { blobName, base64File: logoFile.base64File },
          },
        });
      }

      toast.success(t("settingsClients.clientCreated"));
      if (createdId) {
        history.push(`${basePath}/${createdId}`);
      } else {
        history.push(basePath);
      }
    } catch (error) {
      console.error(error);
      toast.error(t("settingsClients.saveError"));
    } finally {
      setIsSaving(false);
    }
  };

  if (!token) return null;

  return (
    <div className="settings-page-wrapper">
      <PageHeader
        title={t("settingsClients.createModalTitle")}
        showBackButton
        backRoute={basePath}
        backLabel={t("settingsClients.backToList")}
        tabs={[]}
      />

      <div className="settings-content-wrapper pb-28">
        <div className="flex justify-center">
          <div className={PANEL_CARD}>
            <Form
              id="settings-client-create-form"
              className="flex flex-col gap-[26px]"
              onSubmit={handleCreate}
            >
              <ClientLogo
                client={{ name }}
                previewSrc={logoPreviewUrl || null}
                size={84}
                className="!rounded-lg"
                usePlaceholder
              />
              <FormGroup className="mb-0">
                <Label for="create-client-name" className="font-semibold">
                  {t("settingsClients.fieldName")}{" "}
                  <span className="text-[#AF2F3C]">*</span>
                </Label>
                <Input
                  id="create-client-name"
                  className={CLIENT_INPUT_CLASS}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </FormGroup>
              <FormGroup className="mb-0">
                <Label for="create-client-ref" className="font-semibold">
                  {t("settingsClients.fieldReference")}
                </Label>
                <Input
                  id="create-client-ref"
                  className={CLIENT_INPUT_CLASS}
                  value={clientUniqueId}
                  onChange={(e) => setClientUniqueId(e.target.value)}
                />
              </FormGroup>
              <FormGroup className="mb-0">
                <Label for="create-client-address" className="font-semibold">
                  {t("settingsClients.fieldAddress")}
                </Label>
                <Input
                  id="create-client-address"
                  className={CLIENT_INPUT_CLASS}
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                />
              </FormGroup>
              <ClientLogoUpload
                fileName={logoFileName}
                fileSize={logoFileSize}
                isComplete={Boolean(logoFile)}
                onSelectFile={handleLogoSelect}
                onRemove={handleLogoRemove}
                emptyTrigger="pill"
              />
            </Form>
          </div>
        </div>
      </div>

      <div className="sticky bottom-0 z-[2001] w-full bg-white border-t border-[#d3d3d3]">
        <div className="mx-auto max-w-[640px] py-[22px] px-8 flex justify-end items-center gap-2">
          <button
            type="button"
            className="modal-button modal-button-cancel"
            disabled={isSaving}
            onClick={() => history.push(basePath)}
          >
            {t("Cancel")}
          </button>
          <button
            type="submit"
            form="settings-client-create-form"
            className="modal-button modal-button-save"
            disabled={isSaving}
          >
            {t("settingsClients.createButton")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SettingsClientCreateEditor;
